import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Collapse,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import { MouseEventHandler, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router-dom';

import { AppLink } from 'shared/ui/AppLink';

interface NavItem {
  label: string;
  route?: string;
  href?: string;
  icon?: any;
  isHidden?: boolean;
  parentRoute?: string;
  children?: NavItem[];
  isOpenByDefault?: boolean;
}

interface NavListProps {
  items?: NavItem[];
  authIsLoading?: boolean;
}

const matchesRoute = (navItem: NavItem, pathname: string): boolean => {
  const route = navItem.parentRoute ?? navItem.route;
  if (route && matchPath(`${route}/*`, pathname)) return true;
  return (
    navItem.children?.some((child) => matchesRoute(child, pathname)) ?? false
  );
};

const NavItemComponent = ({
  item,
  level = 0,
}: {
  item: NavItem;
  level?: number;
}) => {
  const location = useLocation();
  const [open, setOpen] = useState(item.isOpenByDefault ?? false);

  const isAppLink = !!item.route;
  const activeRoute = item.parentRoute ? item.parentRoute : item.route;
  const isActive = matchPath(activeRoute + '/*', location.pathname);
  const key = item.route ? item.route : item.href;
  const Icon = item.icon;

  const disabledClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
  };

  const hasChildren = item.children && item.children.length > 0;
  const childRouteActive = item.children?.some((child) =>
    matchesRoute(child, location.pathname),
  );
  const isExpanded = Boolean(
    hasChildren && (open || isActive || childRouteActive),
  );

  const handleToggle = () => {
    if (hasChildren) {
      setOpen(!open);
    }
  };

  if (item.isHidden) {
    return null;
  }

  return (
    <>
      <ListItem
        sx={{
          p: 0,
          pl: `${24 + level * 16}px`,
          ':hover': !isActive && {
            bgcolor: 'action.hover',
            cursor: 'pointer',
          },
          bgcolor: isActive && 'primary.main',
          color: isActive && 'common.white',
        }}
        key={key}
      >
        {hasChildren ? (
          <ListItemButton
            onClick={handleToggle}
            sx={{
              p: '12px 0',
              width: '100%',
              bgcolor: isActive && 'primary.main',
              color: isActive && 'common.white',
            }}
          >
            <Icon
              sx={{
                color: isActive ? 'common.white' : 'action.active',
              }}
            />
            <ListItemText
              primary={item.label}
              sx={{
                ml: '12px',
                '& .MuiListItemText-primary': {
                  fontWeight: isActive ? '600' : '400',
                  color: isActive ? 'common.white' : 'action.active',
                },
              }}
            />
            <Box sx={{ pr: '6px', display: 'flex', alignItems: 'center' }}>
              {isExpanded ? (
                <ExpandLess sx={{ color: 'text.secondary' }} />
              ) : (
                <ExpandMore sx={{ color: 'text.secondary' }} />
              )}
            </Box>
          </ListItemButton>
        ) : (
          <Box
            component={isAppLink ? AppLink : Link}
            onClick={isActive ? disabledClick : () => {}}
            to={item.route}
            href={item.href}
            display="flex"
            gap="12px"
            alignItems="center"
            p="12px 0"
            width="100%"
            sx={{ textDecoration: 'none' }}
          >
            <Icon
              sx={{
                color: !isAppLink
                  ? 'common.blue'
                  : isActive
                    ? 'common.white'
                    : 'action.active',
              }}
            />
            <Typography
              fontWeight={!isAppLink ? '600' : ''}
              color={
                !isAppLink
                  ? 'common.blue'
                  : isActive
                    ? 'common.white'
                    : 'action.active'
              }
            >
              {item.label}
            </Typography>
          </Box>
        )}
      </ListItem>

      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children?.map((child, index) => (
              <NavItemComponent
                key={`collapsed-${child.route || child.href || index}`}
                item={child}
                level={level + 1}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export const NavList = memo((props: NavListProps) => {
  const { t } = useTranslation();
  const { items, authIsLoading } = props;

  return (
    <List sx={{ pt: '24px' }}>
      {!authIsLoading ? (
        items?.map((item, index) => (
          <NavItemComponent
            key={`nav-loading-${item.route || item.href || index}`}
            item={item}
          />
        ))
      ) : (
        <Box display="flex" flexDirection="column" gap="2px">
          <Skeleton variant="rectangular" height="48px" />
          <Skeleton variant="rectangular" height="48px" />
          <Skeleton variant="rectangular" height="48px" />
        </Box>
      )}
    </List>
  );
});
